import { useCallback, useState } from 'react';
import { Col, Row } from '@amzn/stencil-react-components/layout';
import { Text } from '@amzn/stencil-react-components/text';
import { Input } from '@amzn/stencil-react-components/input';
import { Button, ButtonSize, ButtonVariant } from '@amzn/stencil-react-components/button';
import { Chip } from '@amzn/stencil-react-components/chip';
import {
  ToggleButton,
  ToggleButtonGroup,
} from '@amzn/stencil-react-components/toggle-button';
// standards-ignore: cross-feature — IconPlusSmall is a generic "add" affordance; a shared wrapper over a bare icon would add indirection without value
import { IconGlobeSmall, IconPlusSmall } from '@amzn/stencil-react-icons';

import { useMessages } from '../../../shared/i18n/useMessages';

const SOP_BUNDLE = 'features.audit-whiz.translations.generateQuestions';

type SopKey =
  | 'sopSectionLabel'
  | 'sopOptionalTag'
  | 'sopSectionDescription'
  | 'sopSelectedLabel'
  | 'sopTabLink'
  | 'sopTabSearch'
  | 'sopTabUpload'
  | 'sopLinkInputPlaceholder'
  | 'sopLinkInputAriaLabel'
  | 'sopLinkAddButton'
  | 'sopLinkBadgeSource'
  | 'sopLinkRemoveAltText'
  | 'sopSearchPlaceholder'
  | 'sopUploadPlaceholder';

/** The three ways to attach an SOP reference. Only `link` is functional in
 *  the Phase 1 shell; `search` and `upload` render placeholder copy. */
export type SopTab = 'link' | 'search' | 'upload';

const TAB_ORDER: readonly SopTab[] = ['link', 'search', 'upload'];

// A Map (not an object-keyed record) so the union-typed `tab` lookup
// doesn't trip security/detect-object-injection.
const TAB_LABEL_KEY = new Map<SopTab, SopKey>([
  ['link', 'sopTabLink'],
  ['search', 'sopTabSearch'],
  ['upload', 'sopTabUpload'],
]);

export interface SopReferenceSectionProps {
  /** The currently attached SOP link, or null when none is set. */
  readonly link: string | null;
  /** Called when the user adds an SOP link. */
  readonly onAddLink: (url: string) => void;
  /** Called when the user removes the attached SOP link. */
  readonly onRemoveLink: () => void;
  /** data-testid prefix for the section's interactive controls. */
  readonly dataTestId?: string;
}

/**
 * The "SOP reference (optional)" block inside the merged "Define your audit
 * questions" card. A three-way source selector (Link / Search Policy / Upload)
 * rendered as a single-select `ToggleButtonGroup` — the honest control for a
 * mutually-exclusive method choice whose selection becomes a value.
 *
 * Card-less: the merged card (owned by the page) supplies the card title and
 * the Source badge. Only the Link option is functional in the Phase 1 shell —
 * the auditor pastes a URL and adds it, at which point the section shows a
 * "Selected SOP" chip. Search and Upload show "coming soon" placeholders.
 *
 * The attached link lives in page state (via `link` / `onAddLink` /
 * `onRemoveLink`); only the local draft URL and the active tab are owned
 * here.
 */
export function SopReferenceSection({
  link,
  onAddLink,
  onRemoveLink,
  dataTestId,
}: SopReferenceSectionProps): JSX.Element {
  const { t } = useMessages<SopKey>(SOP_BUNDLE);
  const [activeTab, setActiveTab] = useState<SopTab>('link');
  const [draftUrl, setDraftUrl] = useState('');

  const hasLink = link !== null;
  const canAdd = draftUrl.trim().length > 0 && !hasLink;

  const handleTabChange = useCallback(
    (_e: React.FormEvent<HTMLElement>, value: string | string[] | undefined) => {
      if (typeof value === 'string') setActiveTab(value as SopTab);
    },
    [],
  );

  const handleAddLink = useCallback(() => {
    const url = draftUrl.trim();
    if (!url) return;
    onAddLink(url);
    setDraftUrl('');
  }, [draftUrl, onAddLink]);

  const handleUrlKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleAddLink();
      }
    },
    [handleAddLink],
  );

  return (
    <Col gridGap="dimensions.spacing.200" width="100%">
      {hasLink ? (
        <Col gridGap="dimensions.spacing.100">
          <Text fontWeight="bold">{t('sopSelectedLabel')}</Text>
          <Row gridGap="dimensions.spacing.200" flexWrap="wrap">
            <Chip
              icon={<IconGlobeSmall aria-hidden="true" />}
              onDismiss={onRemoveLink}
              dismissButtonAltText={t('sopLinkRemoveAltText')}
              data-testid={dataTestId ? `${dataTestId}-link-badge` : undefined}
            >
              <Col>
                <Text>{link}</Text>
                <Text fontSize="T100" color="color.text.secondary">
                  {t('sopLinkBadgeSource')}
                </Text>
              </Col>
            </Chip>
          </Row>
        </Col>
      ) : (
        <>
          <Col gridGap="dimensions.spacing.50">
            <Row alignItems="center" gridGap="dimensions.spacing.50">
              <Text fontWeight="bold">{t('sopSectionLabel')}</Text>
              <Text color="color.text.secondary">{t('sopOptionalTag')}</Text>
            </Row>
            <Text fontSize="T100" color="color.text.secondary">
              {t('sopSectionDescription')}
            </Text>
          </Col>

          {/* Single-select source selector (segmented control) */}
          <ToggleButtonGroup value={activeTab} onChange={handleTabChange}>
            {TAB_ORDER.map((tab) => (
              <ToggleButton
                key={tab}
                value={tab}
                data-testid={dataTestId ? `${dataTestId}-tab-${tab}` : undefined}
              >
                {t(TAB_LABEL_KEY.get(tab) ?? 'sopTabLink')}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>

          {activeTab === 'link' && (
            <Row gridGap="dimensions.spacing.200" alignItems="center">
              <Col flex={1} minWidth={0}>
                <Input
                  type="url"
                  width="100%"
                  value={draftUrl}
                  onChange={(e) => setDraftUrl(e.target.value)}
                  onKeyDown={handleUrlKeyDown}
                  placeholder={t('sopLinkInputPlaceholder')}
                  aria-label={t('sopLinkInputAriaLabel')}
                  data-testid={dataTestId ? `${dataTestId}-link-input` : undefined}
                />
              </Col>
              <Button
                size={ButtonSize.Small}
                variant={ButtonVariant.Secondary}
                icon={<IconPlusSmall aria-hidden="true" />}
                disabled={!canAdd}
                onClick={handleAddLink}
                data-testid={dataTestId ? `${dataTestId}-link-add` : undefined}
              >
                {t('sopLinkAddButton')}
              </Button>
            </Row>
          )}

          {activeTab === 'search' && (
            <Text
              color="color.text.secondary"
              data-testid={dataTestId ? `${dataTestId}-search-placeholder` : undefined}
            >
              {t('sopSearchPlaceholder')}
            </Text>
          )}

          {activeTab === 'upload' && (
            <Text
              color="color.text.secondary"
              data-testid={dataTestId ? `${dataTestId}-upload-placeholder` : undefined}
            >
              {t('sopUploadPlaceholder')}
            </Text>
          )}
        </>
      )}
    </Col>
  );
}
